import React, { useMemo } from 'react';
import { PRINT_COLORS } from '../printTheme';

const PrintFailuresTable = ({ failures, maxRows = 50 }) => {
  // Group failures by type
  const failuresByType = useMemo(() => {
    const grouped = {};
    failures.forEach(f => {
      const type = f.failure_group || 'Other';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(f);
    });
    return grouped;
  }, [failures]);

  // Limit total failures to maxRows
  const limitedFailures = useMemo(() => {
    return failures.slice(0, maxRows);
  }, [failures, maxRows]);

  if (failures.length === 0) {
    return (
      <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
        <h2 style={{ color: PRINT_COLORS.text }}>Unresolved QC Failures</h2>
        <p style={{ color: PRINT_COLORS.pass, fontSize: '14px', fontWeight: 'bold' }}>
          No unresolved failures - All QC metrics passing!
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '20px', background: PRINT_COLORS.background }}>
      <h2 style={{ 
        color: PRINT_COLORS.text, 
        marginBottom: '20px', 
        fontSize: '18px',
        fontWeight: 'bold',
        borderBottom: `2px solid ${PRINT_COLORS.border}`,
        paddingBottom: '10px'
      }}>
        Unresolved QC Failures
      </h2>

      {/* Summary by Type */}
      <div style={{ marginBottom: '20px' }}>
        <h3 style={{ 
          color: PRINT_COLORS.text, 
          fontSize: '14px', 
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Failure Summary
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '10px',
          fontSize: '11px'
        }}>
          {Object.entries(failuresByType).map(([type, items]) => (
            <div 
              key={type}
              style={{ 
                padding: '8px', 
                background: PRINT_COLORS.backgroundLight,
                border: `1px solid ${PRINT_COLORS.border}`,
                borderRadius: '4px'
              }}
            >
              <div style={{ color: PRINT_COLORS.text, marginBottom: '3px', fontSize: '9px' }}>
                {type}
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold', color: PRINT_COLORS.fail }}>
                {items.length}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Failures Table */}
      <div style={{ marginBottom: '15px' }}>
        <h3 style={{ 
          color: PRINT_COLORS.text, 
          fontSize: '14px', 
          marginBottom: '10px',
          fontWeight: 'bold'
        }}>
          Failure Details {failures.length > maxRows && `(Showing first ${maxRows} of ${failures.length})`}
        </h3>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse',
          fontSize: '8px'
        }}>
          <thead>
            <tr style={{ background: PRINT_COLORS.backgroundMedium }}>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Type</th>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Workorder</th>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Sample</th>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Hole ID</th>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Element</th>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>Failure Type</th>
              <th style={{ padding: '6px', textAlign: 'left', border: `1px solid ${PRINT_COLORS.border}`, width: '25%', color: PRINT_COLORS.text }}>Comments</th>
            </tr>
          </thead>
          <tbody>
            {limitedFailures.map((failure, index) => (
              <tr key={index} style={{ background: index % 2 === 0 ? PRINT_COLORS.background : PRINT_COLORS.backgroundLight }}>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>
                  {failure.failure_group || '-'}
                </td>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>
                  {failure.workorder_id || '-'}
                </td>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>
                  {failure.sample_number || failure.dup_sample_number || '-'}
                </td>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>
                  {failure.hole_id || '-'}
                </td>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.text }}>
                  {failure.element || '-'}
                </td>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, color: PRINT_COLORS.fail }}>
                  {failure.failure_type || '-'}
                </td>
                <td style={{ padding: '5px', border: `1px solid ${PRINT_COLORS.border}`, fontSize: '7px', maxWidth: '200px', wordBreak: 'break-word', color: PRINT_COLORS.text }}>
                  {failure.resolution_comments || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {failures.length > maxRows && (
        <div style={{ 
          padding: '10px', 
          background: PRINT_COLORS.backgroundLight,
          border: `1px solid ${PRINT_COLORS.border}`,
          borderRadius: '4px',
          fontSize: '9px',
          color: PRINT_COLORS.text,
          fontStyle: 'italic'
        }}>
          <strong>Note:</strong> Additional {failures.length - maxRows} failures not shown in this report. 
          View the complete list in the interactive dashboard.
        </div>
      )}

      <div style={{ 
        marginTop: '8px',
        padding: '10px', 
        background: PRINT_COLORS.backgroundLight,
        border: `1px solid ${PRINT_COLORS.border}`,
        borderRadius: '4px',
        fontSize: '9px',
        color: PRINT_COLORS.text,
        fontStyle: 'italic'
      }}>
        <strong>Recommendations:</strong> Investigate failures by reviewing sample context, checking for contamination sources, 
        verifying instrument calibration, and assessing lab procedures. Document findings in the resolution comments.
      </div>
    </div>
  );
};

export default PrintFailuresTable;
